/**
 * usePlayerProfileNavigation — navigate from the board to a player's full
 * profile, with the breadcrumb trail and navigation source that screen expects.
 *
 * Three surfaces need this exact navigation and previously open-coded it:
 * the board's small-screen card tap, the preview drawer's "Open profile", and
 * its "View all notes" (SE-5494). Copies drift — one of them forgets
 * `setNavigationSource` and the profile's back button goes somewhere else — so
 * the sequence lives here once.
 *
 * The breadcrumb order is load-bearing: the origin section then the player, both
 * pushed after a `clearBreadcrumbs()`, so returning from a deep player page
 * lands back on the board rather than accumulating a trail across visits.
 *
 * ## The origin crumb is resolved from the CURRENT route, not hardcoded
 *
 * The board is one component rendered at two addresses: `/scouting` embeds it
 * as the Boards tab (`features/scouting`'s MainView imports this very Main),
 * and SE-5509 promoted the same surface to its own `/player-kanban` route. The
 * origin crumb used to say `{ "Boards", "/scouting" }` unconditionally, which
 * was true for the older address and simply wrong for the newer one: opening a
 * player from the pipeline board and pressing back dropped the user on
 * Scouting, a page they had not been on.
 *
 * Reading the pathname at push time makes each entry point name itself, so
 * neither address can be right at the other's expense. Anything not recognised
 * keeps the legacy Boards/Scouting crumb, so no other caller changes.
 * @module features/player-kanban/hooks/usePlayerProfileNavigation
 */

import { type Href, usePathname, useRouter } from "expo-router";
import { useCallback } from "react";

import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";
import {
  type BreadcrumbEntry,
  useNavigationContext,
} from "@/providers/NavigationProvider";

/** The promoted pipeline route (SE-5509) — the board's own address. */
const PIPELINE_PATH = "/player-kanban";

/**
 * Which of the profile's tabs to open on arrival.
 * @remarks
 * The value is the profile route's OWN vocabulary, not this module's: the
 * detail screen reads `?tab=` and maps `"notes"` to its `reportsNotes` tab
 * (`player-detail/screens/Main/MainContainer`, the same param the Add ▸ Note
 * flow already uses with `&action=create`). Only the values that route
 * recognises belong here, so a typo is a type error rather than a silent
 * landing on Overview.
 *
 * `action=create` is deliberately NOT offered — "View all notes" is a read.
 */
export type PlayerProfileTab = "notes";

/** Options for a profile navigation. */
export interface PlayerProfileNavigationOptions {
  /** Tab to activate on arrival. Omit to land on the profile's default. */
  readonly tab?: PlayerProfileTab;
}

/**
 * The origin crumb for a profile opened from the board, named after the address
 * the board was being read at.
 *
 * `/player-kanban` deliberately carries no `?boardId=`: that param is
 * INBOUND-only (`useBoardDeepLink` applies it and manual board switching never
 * writes it back), and the board restores its own active board on return, so
 * pinning the id the user happened to arrive with could send them back to a
 * different board than the one they left.
 * @param pathname - The route the navigation is being started from.
 * @returns The breadcrumb entry to seed the trail with.
 */
export const resolveBoardOriginCrumb = (pathname: string): BreadcrumbEntry =>
  pathname === PIPELINE_PATH || pathname.startsWith(`${PIPELINE_PATH}/`)
    ? { label: "Pipeline", path: PIPELINE_PATH }
    : /*
       * "Boards" is NOT stale terminology to be swept up in the Pipeline
       * rename. That rename promoted THIS surface at its own address; the
       * scouting page still shows the board under a tab literally labelled
       * "Boards" (`SCOUTING_TABS[1]`, pinned by its own test). A crumb naming
       * the tab the user actually pressed is the correct one here.
       */
      { label: "Boards", path: "/scouting" };

/**
 * Build a navigator to a player's profile page.
 * @remarks
 * The optional `tab` lands the user on a specific tab instead of the profile's
 * default. It exists because "View all notes" was wired straight to this
 * navigator and therefore dropped the user on Overview, one more click from the
 * list they had asked for (Raniel, 2026-08-07). The targeting rides on the
 * route's existing `?tab=` param rather than a new mechanism.
 *
 * The BREADCRUMB keeps the bare player URL while the push carries the param:
 * the crumb names the player's page, which is one destination however it was
 * entered, and pinning a tab into the trail would make "back" reopen a tab
 * rather than a page.
 * @returns A stable navigator taking a player id and optional tab targeting.
 */
export const usePlayerProfileNavigation = (): ((
  playerId: string,
  options?: PlayerProfileNavigationOptions
) => void) => {
  const router = useRouter();
  const pathname = usePathname();
  const getExpandedUrl = useGetExpandedPlayerUrl();
  const { pushBreadcrumb, clearBreadcrumbs, setNavigationSource } =
    useNavigationContext();

  return useCallback(
    (playerId: string, options?: PlayerProfileNavigationOptions) => {
      const playerUrl = getExpandedUrl(`/players/${playerId}`) as string;
      const target = options?.tab
        ? `${playerUrl}?tab=${options.tab}`
        : playerUrl;

      clearBreadcrumbs();
      pushBreadcrumb(resolveBoardOriginCrumb(pathname));
      pushBreadcrumb({ label: "Player", path: playerUrl });
      setNavigationSource("kanban");

      router.push(target as Href);
    },
    [
      router,
      pathname,
      getExpandedUrl,
      pushBreadcrumb,
      clearBreadcrumbs,
      setNavigationSource,
    ]
  );
};
